import React, { PureComponent } from 'react'
import moment from 'moment'
import { withRouter } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { get } from 'lodash'

// actions
import {
  getContactsByOrganization,
  getContactMatches,
  mergeContact,
  searchContacts,
  clearSearchResults,
  openModal,
  closeModal,
  createToast,
  statusType
} from '../../../actions'

// components
import { Banner, Spinner } from '../../../components'
import CustomContactToolbar from './toolbar/toolbar.component'
import CustomContactGrid from './grid/grid.component'
import ContactMatch from './match/match.component'

import { getPageSize, THEMES } from '../../../utils'
import { isEmpty, isEqual } from 'lodash'

/**
 * Custom Contact Detail Page Component
 */
class CustomContactDetailPage extends PureComponent {
  /**
   * Constructor
   * @param props
   */
  constructor(props) {
    super(props)
    this.state = {
      security: {},
      contacts: [],
      contact: {},
      showContactMatchModal: false,
      isMatchesLoading: false,
      filters: {
        query: '',
        limit: getPageSize('custom-contact-grid') || 10,
        page: 1
      }
    }

    this.handleFilterChange = this.handleFilterChange.bind(this)
  }

  /**
   * Component Did Update
   * @param prevProps
   * @param prevState
   */
  componentDidUpdate(prevProps, prevState) {
    const { customContacts: { data, security, total, status }, matches, match } = this.props
    const { customContacts: { prevData, prevSecurity, prevStatus }, prevMatches } = prevProps
    const { contacts } = this.state
    const id = get(match, 'params.id')
    const orgMatches = matches && matches[id]
    const prevOrgMatches = prevMatches && prevMatches[id]

    // set security information to state to prevent banner re-render
    if (security !== prevSecurity && !isEmpty(security)) {
      this.setState({ security })
    }

    // Check for changes in contact data
    if (!isEqual(data, prevData) && status !== prevStatus && status !== statusType.IN_PROGRESS) {
      const mapped = this.mapMatches(data, get(orgMatches, 'data'))
      const isChanged = !isEqual(mapped, contacts)
      isChanged && this.setState({ contacts: mapped, total })
    }

    // Check if Cortera matches have been updated
    if (!isEqual(get(orgMatches, 'data'), get(prevOrgMatches, 'data'))) {
      const mapped = this.mapMatches(contacts, get(orgMatches, 'data'))
      const isChanged = !isEqual(mapped, contacts)
      isChanged && this.setState({ contacts: mapped })
    }
  }

  /**
   * Component Did Mount
   */
  componentDidMount() {
    this.getContacts()
  }

  /**
   * Get Custom Contacts
   */
  getContacts() {
    const { filters } = this.state
    const { getContacts, match } = this.props
    const id = match && match.params && match.params.id

    id && getContacts(id, filters)
  }

  /**
   * Map matched Cortera contacts to organization custom contacts
   * @private
   */
  mapMatches(contacts, matches) {
    if (!contacts || !contacts.length || !matches || !matches.length) {
      return contacts
    }

    return (contacts || []).map((contact) => {
      if (!contact || !contact._id) {
        return contact
      }
      const match = matches.find((match) => match && match.request && match.request.contact_id === contact._id)
      return {
        ...contact,
        _match: match && match.result,
        score: match && match.score
      }
    })
  }

  /**
   * Map Loading State
   * Set score to 'In Progress' while loading
   * @param contacts
   */
  mapLoadingState(contacts) {
    const { match, matches } = this.props
    const id = get(match, 'params.id')
    const orgMatches = matches && matches[id]
    const isLoading = orgMatches && orgMatches.isLoading

    return (contacts || []).map((contact) => ({
      ...contact,
      score: contact.score || (isLoading && 'In Progress')
    }))
  }

  /**
   * Handle page and query change
   * @param filters
   */
  handleFilterChange(filters) {
    this.setState(
      { filters: { ...this.state.filters, ...filters } },
      () => filters.hasOwnProperty('query') && this.getContacts()
    )
  }

  /**
   * Get label bases on progress
   */
  getMatchesButtonLabel = () => {
    const { match, matches } = this.props
    const id = get(match, 'params.id')
    const orgMatches = matches && matches[id]
    const isMatchesLoading = get(orgMatches, 'isLoading')
    const percentComplete = get(orgMatches, 'percentComplete')
    const requestDate = get(orgMatches, 'requestDate')

    if (isMatchesLoading && Number(percentComplete) < 100) {
      return `In Progress: ${percentComplete}%`
    }
    else if (percentComplete === 100) {
      return `Last Sync: ${moment(requestDate).format('YYYY-MM-DD h:mm A')}`
    }
    else {
      return null
    }

  }

  /**
   * Get Contact Matches
   */
  getMatches = () => {
    const { getMatches, userId, match } = this.props
    const securityId = match && match.params && match.params.id
    getMatches({ securityId, userId })
  }

  /**
   * Render
   * @return {*}
   */
  render() {
    const { filters, security, contacts, total, contact, showContactMatchModal } = this.state
    const {
      customContacts: { initialLoading, status }, match, matches,
      loadingSuggestions, suggestions, searchContacts, clearSearchResults,
      openModal, closeModal, createToast, mergeContact, mergeStatus
    } = this.props
    const id = get(match, 'params.id')
    const orgMatches = matches && matches[id]
    const isMatchesLoading = orgMatches && orgMatches.isLoading
    const buttonLabel = this.getMatchesButtonLabel()

    return (
      <>
        <Banner
          icon='q4i-security-2pt'
          title={security.name || ''}
          details={
            security.symbol
              ? <span>{security.symbol}<span className='divider'></span>{security.exchange}</span>
              : null}
          controls={[
            {
              type: 'button',
              theme: THEMES.CITRUS,
              icon: 'q4i-sync-or-fetch-4pt',
              disabled: isMatchesLoading,
              square: !buttonLabel,
              label: buttonLabel,
              onClick: this.getMatches
            }
          ]} />
        <div className='page_grid'>
          {(status === statusType.IN_PROGRESS) && <Spinner mask theme={THEMES.RAIN} />}
          <CustomContactToolbar
            handleFilterChange={this.handleFilterChange} />
          <CustomContactGrid
            isGridReady={!initialLoading}
            data={this.mapLoadingState(contacts)}
            filters={filters}
            handleFilterChange={this.handleFilterChange}
            total={total}
            changeContactMatch={(contact) => this.setState({ contact, showContactMatchModal: true })} />
        </div>

        {showContactMatchModal && <ContactMatch
          contact={contact}
          loadingSuggestions={loadingSuggestions}
          suggestions={suggestions}
          searchContacts={searchContacts}
          clearSearchResults={clearSearchResults}
          onClose={() => this.setState({ showContactMatchModal: false })}
          onSuccess={this.getContacts.bind(this)}
          openModal={openModal}
          closeModal={closeModal}
          createToast={createToast}
          mergeContact={mergeContact}
          mergeStatus={mergeStatus}
        />}
      </>
    )
  }
}

const mapStateToProps = (state) => {
  const search = get(state, 'contact.search')

  return {
    customContacts: get(state, 'custom.contacts'),
    matches: get(state, 'custom.matches'),
    mergeStatus: get(state, 'custom.merge.status'),
    loadingSuggestions: search.status === statusType.IN_PROGRESS,
    suggestions: search.data,
    userId: get(state, 'profile.userId')
  }
}

const mapDispatchToProps = (dispatch) => ({
  getContacts: bindActionCreators(getContactsByOrganization, dispatch),
  getMatches: bindActionCreators(getContactMatches, dispatch),
  mergeContact: bindActionCreators(mergeContact, dispatch),
  searchContacts: bindActionCreators(searchContacts, dispatch),
  clearSearchResults: bindActionCreators(clearSearchResults, dispatch),
  openModal: bindActionCreators(openModal, dispatch),
  closeModal: bindActionCreators(closeModal, dispatch),
  createToast: bindActionCreators(createToast, dispatch)
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CustomContactDetailPage))
